import { normalizeUiLang, SUPPORTED_UI_LANGS, type UiLang } from "site/services/i18n";

export const LESSON_TRANSLATION_LANGS = SUPPORTED_UI_LANGS;
export type LessonTranslationLang = UiLang;
export type LessonTranslations = Record<LessonTranslationLang, string>;

export function emptyLessonTranslations(): LessonTranslations {
  return { ru: "", en: "", fr: "" };
}

export function lessonTranslationLangIndex(lang: LessonTranslationLang): number {
  return LESSON_TRANSLATION_LANGS.indexOf(lang);
}

function isLessonTranslationsRecord(value: unknown): value is Partial<LessonTranslations> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  return LESSON_TRANSLATION_LANGS.some((lang) => lang in (value as Record<string, unknown>));
}

export function parseLessonTranslationsPart(value: unknown): LessonTranslations {
  const out = emptyLessonTranslations();
  if (typeof value === "string") {
    const text = value.trim();
    if (text) {
      out.ru = text;
    }
    return out;
  }
  if (isLessonTranslationsRecord(value)) {
    for (const lang of LESSON_TRANSLATION_LANGS) {
      const item = value[lang];
      if (typeof item === "string") {
        out[lang] = item;
      }
    }
    return out;
  }
  if (!Array.isArray(value)) {
    return out;
  }
  for (let i = 0; i < LESSON_TRANSLATION_LANGS.length; i += 1) {
    const item = value[i];
    if (typeof item === "string") {
      out[LESSON_TRANSLATION_LANGS[i]!] = item;
    }
  }
  return out;
}

export function serializeLessonTranslationsPart(
  translations: Partial<LessonTranslations>,
  normalizeRu?: (text: string) => string,
): string[] {
  const parts = LESSON_TRANSLATION_LANGS.map((lang) => {
    let text = String(translations[lang] ?? "").trim();
    if (lang === "ru" && normalizeRu) {
      text = normalizeRu(text).trim();
    }
    return text;
  });
  while (parts.length > 1 && parts[parts.length - 1] === "") {
    parts.pop();
  }
  return parts.some(Boolean) ? parts : [];
}

export function pickLessonTranslation(
  value: unknown,
  lang: string | null | undefined,
  fallbackLang: LessonTranslationLang = "ru",
): string {
  const translations = parseLessonTranslationsPart(value);
  const uiLang = normalizeUiLang(String(lang ?? fallbackLang));
  const picked = translations[uiLang]?.trim();
  if (picked) {
    return picked;
  }
  if (uiLang !== fallbackLang) {
    const fallback = translations[fallbackLang]?.trim();
    if (fallback) {
      return fallback;
    }
  }
  for (const code of LESSON_TRANSLATION_LANGS) {
    const text = translations[code]?.trim();
    if (text) {
      return text;
    }
  }
  return "";
}

export type LessonTitleSource = {
  title_russian?: string | null;
  title_en?: string | null;
  title_fr?: string | null;
  title_translations?: unknown;
  title_arabic?: string | null;
};

export function parseLessonTitleTranslations(source: LessonTitleSource): LessonTranslations {
  const out = emptyLessonTranslations();
  out.ru = String(source.title_russian ?? "").trim();
  out.en = String(source.title_en ?? "").trim();
  out.fr = String(source.title_fr ?? "").trim();
  if (source.title_translations != null) {
    const parsed = parseLessonTranslationsPart(source.title_translations);
    for (const lang of LESSON_TRANSLATION_LANGS) {
      if (!out[lang]?.trim() && parsed[lang]?.trim()) {
        out[lang] = parsed[lang]!;
      }
    }
  }
  if (!out.ru) {
    const legacy = parseLessonTranslationsPart(source.title_translations);
    if (legacy.ru?.trim()) {
      out.ru = legacy.ru;
    }
  }
  return out;
}

export function lessonTitleFieldsFromTranslations(
  translations: Partial<LessonTranslations>,
  normalizeRu?: (text: string) => string,
): { title_russian: string; title_en?: string; title_fr?: string } {
  let titleRussian = String(translations.ru ?? "").trim();
  if (normalizeRu) {
    titleRussian = normalizeRu(titleRussian).trim();
  }
  const titleEn = String(translations.en ?? "").trim();
  const titleFr = String(translations.fr ?? "").trim();
  const out: { title_russian: string; title_en?: string; title_fr?: string } = {
    title_russian: titleRussian,
  };
  if (titleEn) {
    out.title_en = titleEn;
  }
  if (titleFr) {
    out.title_fr = titleFr;
  }
  return out;
}

export function pickLessonTitle(
  exercise: LessonTitleSource | null | undefined,
  lang: string | null | undefined,
): string {
  if (!exercise) {
    return "";
  }
  const translations = parseLessonTitleTranslations(exercise);
  const picked = pickLessonTranslation(translations, lang);
  if (picked) {
    return picked;
  }
  return String(exercise.title_arabic ?? "").trim();
}

export function rowTranslationForLang(
  row: { russian: string; translationEn?: string; translationFr?: string },
  lang: LessonTranslationLang,
): string {
  if (lang === "en") {
    return row.translationEn ?? "";
  }
  if (lang === "fr") {
    return row.translationFr ?? "";
  }
  return row.russian ?? "";
}

export function setRowTranslationForLang(
  row: { russian: string; translationEn?: string; translationFr?: string },
  lang: LessonTranslationLang,
  text: string,
): void {
  if (lang === "en") {
    row.translationEn = text;
    return;
  }
  if (lang === "fr") {
    row.translationFr = text;
    return;
  }
  row.russian = text;
}

export function rowToLessonTranslations(
  row: { russian: string; translationEn?: string; translationFr?: string },
): LessonTranslations {
  return {
    ru: row.russian ?? "",
    en: row.translationEn ?? "",
    fr: row.translationFr ?? "",
  };
}

export function applyLessonTranslationsToRow(
  row: { russian: string; translationEn?: string; translationFr?: string },
  translations: Partial<LessonTranslations>,
): void {
  for (const lang of LESSON_TRANSLATION_LANGS) {
    if (translations[lang] !== undefined) {
      setRowTranslationForLang(row, lang, translations[lang] ?? "");
    }
  }
}
